import React, { useEffect, useState } from 'react';
import { Switch } from '@mui/material';
import ChecklistRtlOutlinedIcon from '@mui/icons-material/ChecklistRtlOutlined';
import { DataProviderInstance } from '../../api/DataProvider';
import AreaTitle from '../../components/areaTitle/AreaTitle';
import ConfirmPopup from '../../popup/ConfirmPopup/ConfirmPopup';
import Toast from '../../components/support/Toast';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { USER_ROLES } from '../../constants/userRoles';
import StepStatusCard from './components/StepStatusCard';
import { STEP_STATUSES } from './appointmentSteps';
import styles from './AppointmentStepsPage.module.css';

const ERROR_TEXT = 'Não foi possível salvar. Tente de novo.';

/**
 * Cadastros > Etapas do atendimento: o checklist e a cor de cada status do
 * agendamento. As ativas ficam em CompanyConfig.AppointmentSteps e as cores em
 * CompanyConfig.AppointmentStatusColors (o DataProvider atualiza depois de cada
 * gravação); as desativadas só aparecem com "Exibir inativas".
 */
const AppointmentStepsPage = () => {
    const {
        CompanyConfig, GetAppointmentSteps, GetInactiveAppointmentSteps,
        SetAppointmentStep, SetAppointmentStepActive, ReorderAppointmentSteps, SetAppointmentStatusColor,
    } = DataProviderInstance();
    const steps = CompanyConfig?.AppointmentSteps || [];
    const statusColors = CompanyConfig?.AppointmentStatusColors || {};
    // Criar, editar, reordenar e desativar: só proprietário e administrador (RoleAccessMap)
    const { hasRole } = useRoleAccess();
    const canManage = hasRole([USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN]);

    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);
    const [showInactive, setShowInactive] = useState(false);
    const [inactiveSteps, setInactiveSteps] = useState([]);
    const [deactivateTarget, setDeactivateTarget] = useState(null);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    const showToast = (message, severity = 'success') => setToast({ open: true, message, severity });

    // Só na abertura: as funções do DataProvider mudam a cada render
    useEffect(() => {
        GetAppointmentSteps(() => setLoaded(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadInactive = () => GetInactiveAppointmentSteps(setInactiveSteps);

    useEffect(() => {
        if (showInactive) loadInactive();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showInactive]);

    // Grava e avisa; onDone recebe true quando deu certo (o formulário da linha fecha)
    const run = (call, successMessage, onDone) => {
        setBusy(true);
        call((response) => {
            setBusy(false);
            const ok = response?.status === 'SUCCESS';
            if (ok) {
                if (successMessage) showToast(successMessage);
                if (showInactive) loadInactive();
            } else {
                showToast(response?.error || ERROR_TEXT, 'error');
            }
            if (onDone) onDone(ok);
        });
    };

    const handleSave = (data, onDone) => {
        run((cb) => SetAppointmentStep(data, cb), data.id ? 'Etapa atualizada' : 'Etapa criada', onDone);
    };

    const handleMove = (status, ids) => {
        run((cb) => ReorderAppointmentSteps(status, ids, cb));
    };

    const handleConfirmDeactivate = () => {
        const target = deactivateTarget;
        setDeactivateTarget(null);
        run((cb) => SetAppointmentStepActive(target.Id, false, cb), 'Etapa desativada');
    };

    const handleRestore = (step) => {
        run((cb) => SetAppointmentStepActive(step.Id, true, cb), 'Etapa reativada');
    };

    const handleStatusColor = (status, color) => {
        run((cb) => SetAppointmentStatusColor(status, color, cb), 'Cor atualizada');
    };

    return (
        <div className={`page-content ${styles.container}`}>
            <Toast
                open={toast.open}
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                message={toast.message}
                severity={toast.severity}
                autoHideDuration={5000}
            />
            {deactivateTarget && (
                <ConfirmPopup
                    Visibility
                    Title="Desativar etapa"
                    Description={`Deseja desativar a etapa "${deactivateTarget.Name}"? Ela deixa de aparecer nos agendamentos; o que já foi marcado continua no histórico.`}
                    ConfirmLabel="Desativar"
                    OnConfirm={handleConfirmDeactivate}
                    OnHide={() => setDeactivateTarget(null)}
                />
            )}

            <div className={styles.header}>
                <AreaTitle
                    Title="Etapas do atendimento"
                    Description="O que a equipe confere em cada status, como um checklist. O status do agendamento não muda."
                    Icon={<ChecklistRtlOutlinedIcon />}
                />
                <label className={styles.showInactive}>
                    <Switch
                        size="small"
                        checked={showInactive}
                        onChange={(e) => setShowInactive(e.target.checked)}
                    />
                    <span>Exibir inativas</span>
                </label>
            </div>

            <div className={styles.grid}>
                {STEP_STATUSES.map((status) => (
                    <StepStatusCard
                        key={status.id}
                        status={status}
                        loading={!loaded}
                        steps={steps.filter((step) => step.Status === status.id)}
                        inactiveSteps={showInactive ? inactiveSteps.filter((step) => step.Status === status.id) : []}
                        statusColor={statusColors[status.id] || null}
                        canManage={canManage}
                        busy={busy}
                        onSave={handleSave}
                        onMove={handleMove}
                        onDeactivate={setDeactivateTarget}
                        onRestore={handleRestore}
                        onStatusColor={handleStatusColor}
                    />
                ))}
            </div>
        </div>
    );
};

export default AppointmentStepsPage;
